/**
 * „Hey Vespera, wie wird das Wetter morgen in Hollenthon" — in EINEM Atemzug.
 * (24.09.2026, Ticket 2536)
 *
 * Martin möchte sprechen wie mit „Hey Siri, …": Wach-Wort und Frage ohne
 * Absetzen. Bis Build 76 meldete der Lauscher nur den Anlass („hey vespera"),
 * die Seite öffnete das Mikrofon neu (1,7 s) — und der Satz, der schon
 * gesprochen war, war weg.
 *
 * Ab Build 77 bringt der Lauscher den Satz mit: den Text und den Ton NACH dem
 * Wach-Wort (PawbotWachWort.swift schneidet am Ende des Wach-Worts; was davor
 * gesagt wurde, verlässt das Gerät nicht). Hier wird nur noch entschieden, ob
 * er gesendet wird oder ob — wie bisher — das Mikrofon aufgeht.
 *
 * ⚠️ EIN Sendeweg: der Satz geht über die Sprechtaste (pttSendBlob in
 *    app.ts), mit dem Ton als Aufnahme und dem Text als fertigem Satz. Kein
 *    Mikro-Start, kein Fenster, keine Mitschrift.
 * ⚠️ Ältere Builds (74–76) schicken nur `wort` — dann gilt der alte Weg,
 *    unverändert.
 */
import { base64ZuBlob } from "./base64-blob.js";
/*
 * Nacharbeit 24.09.2026: DERSELBE Filter wie am Server, keine Kopie seiner
 * Zahlen. Das Modul ist rein (keine Importe), die Seite zieht es wie
 * server/models.js mit in ihr Bündel.
 */
import { istKurzesPhantom } from "../../server/kurz-phantom.js";
import { satzOffen } from "../../shared/satz-vollstaendig.js";

/** Was der Lauscher nach dem Wach-Wort mitbringt (ab Build 77). */
export interface WachwortSatz {
  /** Auf dem Gerät erkannt — NUR was nach dem Wach-Wort kam. */
  text: string;
  /** Der Ton desselben Satzes (AAC), base64 — ab dem Ende des Wach-Worts. */
  tonBase64: string;
  mime: string;
  /** Länge des Tons; 0 = unbekannt. */
  dauerMs: number;
  /**
   * Wann der Satz auf dem Gerät zu Ende war (Epoch-ms, Uhr des Geräts);
   * 0 = unbekannt. (Nacharbeit 24.09.2026) Daran misst die Vormerkung sein
   * Alter — nicht an der Ankunft, die sich verspäten kann.
   */
  am: number;
}

export interface WachwortEreignis {
  wort: string;
  satz: WachwortSatz | null;
}

/**
 * Das Ereignis `wachwort` der nativen Hülle lesen.
 *
 * ⚠️ Ein Satz zählt nur, wenn Text UND Ton da sind. Fehlt eines, ist es der
 *    alte Anlass — auch bei einem Build, der nur halb liefert.
 */
export function wachwortEreignisLesen(d: unknown): WachwortEreignis {
  const o = d && typeof d === "object" ? (d as Record<string, unknown>) : {};
  const wort = "wort" in o ? String(o.wort) : "";
  const text = typeof o.satz === "string" ? o.satz : "";
  const ton = typeof o.ton === "string" ? o.ton : "";
  if (!text.trim() || !ton) return { wort, satz: null };
  const mime =
    typeof o.mime === "string" && o.mime.startsWith("audio/") ? o.mime : "audio/mp4";
  const dauer = Number(o.dauerMs);
  const am = Number(o.am);
  return {
    wort,
    satz: {
      text,
      tonBase64: ton,
      mime,
      dauerMs: Number.isFinite(dauer) && dauer > 0 ? Math.round(dauer) : 0,
      am: Number.isFinite(am) && am > 0 ? Math.round(am) : 0,
    },
  };
}

/**
 * Weniger Wörter sind kein Satz, sondern Rauschen oder ein Füllwort — dann
 * lieber das Mikrofon öffnen und fragen lassen.
 *
 * ⚠️ Zwei, nicht drei: „Wie spät?" ist eine ganze Frage (und genau das, wofür
 *    die Schnellspur da ist) — sofern der Ton dem Server lang genug ist,
 *    siehe `kurz-phantom` in `wachwortPlan`.
 */
const SATZ_MIN_WOERTER = 2;

/**
 * Darunter verwirft der Server den Ton als leer (MIN_AUDIO_BYTES in
 * server/voice-command.ts) — dann gar nicht erst senden.
 */
export const SATZ_MIN_BYTES = 512;

/** Warum statt des Satzes das Mikrofon aufgeht. */
type WachwortMikroGrund =
  | "ohne-satz"
  | "zu-kurz"
  | "ton-kaputt"
  | "ton-leer"
  | "kurz-phantom"
  | "anruf-modus"
  | "bild-wartet";

type WachwortPlan =
  | { art: "senden"; ton: Blob; mime: string; text: string; dauerMs: number }
  | { art: "mikro"; grund: WachwortMikroGrund }
  /** 20.10.2026 (Vollständigkeits-Bremse): erkennbar offener Satz — nicht senden, ins Eingabefeld. */
  | { art: "feld"; text: string };

interface WachwortLage {
  /** Sprechtaste steht auf „Anruf" — das Wach-Wort startet dann den Anruf. */
  anrufModus: boolean;
  /** Ein Bild wartet im Eingabefeld — das Gesprochene gehört dazu. */
  bildWartet: boolean;
}

function woerter(text: string): number {
  return text
    .trim()
    .split(/\s+/)
    .filter((w) => /\p{L}/u.test(w)).length;
}

/**
 * Senden oder Mikrofon? Rein — kein Zugriff auf Seite, Netz oder Mikrofon.
 *
 * ⚠️ Im Zweifel das Mikrofon: das ist der Weg bis Build 76 und verliert
 *    nichts außer dem Satz, der ohnehin nicht taugte.
 */
export function wachwortPlan(
  satz: WachwortSatz | null,
  lage: WachwortLage,
  alsBlob: (b64: string, mime: string) => Blob = base64ZuBlob,
): WachwortPlan {
  if (!satz) return { art: "mikro", grund: "ohne-satz" };
  /*
   * 20.10.2026 (Vollständigkeits-Bremse, shared/satz-vollstaendig.ts; Martin: „Es kann nie abgeschickt werden, wenn der
   * Satz unvollständig ist“): die Erkennung des Geräts macht Schluss, wo SIE eine Pause hört. Endet der Satz erkennbar
   * offen („… und“, „…, dass“, „wenn das Swoopband“), geht er nicht ab — er kommt ins Eingabefeld, VOR jedem anderen
   * Grund (Anruf-Modus, wartendes Bild, zu kurz, Ton): dort ginge er sonst mit dem Mikro-Weg verloren. Nur `offen`:
   * ohne Satzzeichen liefert das Gerät oft auch ganze Fragen („wie wird das Wetter morgen in Graz“).
   */
  const offen = offenerWachwortSatz(satz);
  if (offen !== null) return { art: "feld", text: offen };
  /*
   * Im Anruf-Modus öffnet die Sprechtaste einen Anruf, keine Aufnahme. Ein
   * gesendeter Satz ginge daran vorbei — dort gilt, was der Nutzer gewählt hat.
   */
  if (lage.anrufModus) return { art: "mikro", grund: "anruf-modus" };
  /*
   * Wartet ein Bild, landet das Gesprochene im Textfeld (pttFinishAndSend,
   * 05.09.2026). Der Satz nach dem Wach-Wort hat diese Weiche nicht; der
   * alte Weg schon.
   */
  if (lage.bildWartet) return { art: "mikro", grund: "bild-wartet" };
  const text = satz.text.trim();
  if (woerter(text) < SATZ_MIN_WOERTER) return { art: "mikro", grund: "zu-kurz" };
  let ton: Blob;
  try {
    ton = alsBlob(satz.tonBase64, satz.mime);
  } catch {
    return { art: "mikro", grund: "ton-kaputt" };
  }
  if (ton.size < SATZ_MIN_BYTES) return { art: "mikro", grund: "ton-leer" };
  /*
   * ⚠️ Nacharbeit 24.09.2026: Der Server verwirft eine Aufnahme unter
   *    8.000 Bytes mit höchstens zwei Wörtern, die keine Kurzantwort sind
   *    (server/kurz-phantom.ts, Ticket 2443) — und antwortet „nichts
   *    verstanden", ohne dass das Mikro aufgeht. „Wie spät?" als AAC ist
   *    rund eine Sekunde lang und liegt, je nach Eingang (Bluetooth: 16 kHz),
   *    genau dort. Was der Server sicher verwürfe, geht darum gar nicht erst
   *    ab: das Mikro öffnet sich wie bisher, und die Frage kann noch einmal
   *    gestellt werden. Geprüft wird mit DERSELBEN Funktion, am Text vom
   *    Gerät (der Server prüft seinen eigenen — beide sind Erkennungen
   *    desselben Tons).
   */
  if (istKurzesPhantom({ bytes: ton.size, transcript: text })) {
    return { art: "mikro", grund: "kurz-phantom" };
  }
  return { art: "senden", ton, mime: satz.mime, text, dauerMs: satz.dauerMs };
}

/** Die zwei Wege, die app.ts anbietet — beide gab es schon vorher. */
export interface WachwortWege {
  /** Über pttSendBlob, ohne Mikrofon. */
  sendeSatz(s: { ton: Blob; mime: string; text: string; dauerMs: number }): void;
  /** startPttHandsFree("wachwort") — der Weg bis Build 76. */
  oeffneMikro(): void;
  /**
   * 20.10.2026 (Vollständigkeits-Bremse): den Text ins Eingabefeld legen und es sagen. Fehlt der Weg, öffnet sich das
   * Mikro wie bisher — gesendet wird ein offener Satz nie.
   */
  insFeld?(text: string): void;
  /**
   * Weg von jeder anderen Seite, hin zum Chat — VOR beiden Wegen. (06.10.2026, Martin: „wenn ich auf der Seite im
   * Essenz-Spiegel oder woanders bin und ‚Hey Vespera‘ sage, soll das System auf Chat gehen, sonst ist das Modal im
   * Hintergrund“.) Eine Vollseite wie der Essenz-Spiegel liegt über der Aufnahmekarte; die Antwort erschiene unter ihr.
   */
  zumChat(): void;
  /** Eine Zeile ins Diagnoseprotokoll — nie mit dem Text selbst. */
  melde(zeile: string): void;
}

/**
 * Was nach dem Wach-Wort geschieht — EINE Stelle für beide Wege.
 * Gibt zurück, was gewählt wurde (für Tests und Protokoll).
 */
export function wachwortAusfuehren(
  satz: WachwortSatz | null,
  lage: WachwortLage,
  wege: WachwortWege,
  alsBlob: (b64: string, mime: string) => Blob = base64ZuBlob,
): WachwortPlan["art"] {
  const plan = wachwortPlan(satz, lage, alsBlob);
  // Die Seite wechseln darf nie verhindern, dass das Wach-Wort etwas tut: ein Fehler hier lässt beide Wege offen.
  try {
    wege.zumChat();
  } catch {
    /* weiter wie bisher */
  }
  if (plan.art === "senden") {
    wege.sendeSatz({ ton: plan.ton, mime: plan.mime, text: plan.text, dauerMs: plan.dauerMs });
    return "senden";
  }
  if (plan.art === "feld") {
    const ziel = wege.insFeld ? "ins Eingabefeld" : "Mikro wie bisher";
    wege.melde(`[wachwort] satz nicht gesendet grund=unvollstaendig zeichen=${plan.text.length} — ${ziel}`);
    if (wege.insFeld) {
      wege.insFeld(plan.text);
      return "feld";
    }
    wege.oeffneMikro();
    return "mikro";
  }
  // Nur wenn ein Satz DA war, ist das Verwerfen eine Nachricht wert.
  if (satz) {
    wege.melde(
      `[wachwort] satz nicht gesendet grund=${plan.grund} zeichen=${satz.text.trim().length} — Mikro wie bisher`,
    );
  }
  wege.oeffneMikro();
  return "mikro";
}

/** Der Text eines Satzes nach dem Wach-Wort, wenn er erkennbar offen ist (`satzOffen` = `offen`) — sonst `null`. */
function offenerWachwortSatz(satz: WachwortSatz | null): string | null {
  const text = satz?.text.trim() ?? "";
  return text && satzOffen(text) === "offen" ? text : null;
}

/**
 * Prüfauflage H1 (20.10.2026): was beim Einlösen ins Eingabefeld gehört — der offene Satz, auch wenn die Vormerkung
 * verfallen ist (30 s), eine Wiedergabe läuft oder die Sprechtaste belegt ist. Gesendet wird er nie; still verworfen auch
 * nicht. `null`: nichts fürs Feld (kein Satz, fertiger Satz) — dann gilt alles wie bisher.
 */
export function wachwortFeldText(e: WachwortEinloesung | null): string | null {
  if (!e) return null;
  return e.art === "verfallen" ? e.feld : offenerWachwortSatz(e.satz);
}

/** Merkmale für die Zeile „[wachwort] gefallen …" — Länge, nie Inhalt. */
export function satzMerkmale(satz: WachwortSatz | null): string {
  if (!satz) return "satz=nein";
  return `satz=ja ton=${satz.dauerMs}ms zeichen=${satz.text.trim().length}`;
}

/**
 * So lange gilt ein Wach-Wort (und sein Satz), das die Seite nicht sofort
 * einlösen kann. (23.09.2026 in app.ts, seit der Nacharbeit 24.09.2026 hier)
 */
export const WACHWORT_NACHHOLEN_MS = 30_000;

/** Ein nachgeholtes Wort (im Hintergrund gefallen) darf eine inzwischen laufende Wiedergabe nicht abbrechen. */
export function wachwortNachholenStoertWiedergabe(nachgeholt: boolean, wiedergabeLaeuft: boolean): boolean {
  return nachgeholt && wiedergabeLaeuft;
}

/**
 * Was beim Einlösen herauskommt. ⚠️ `verfallen` trägt KEINEN Satz — wer im
 * Verfallszweig etwas senden wollte, hätte nichts in der Hand. Nur den Text
 * eines OFFENEN Satzes (`feld`, Prüfauflage H1 20.10.2026): er gehört ins
 * Eingabefeld, nie an die KI.
 */
type WachwortEinloesung =
  | { art: "weiter"; satz: WachwortSatz | null; nachMs: number }
  | { art: "verfallen"; nachMs: number; feld: string | null };

/**
 * Das gefallene Wach-Wort, bis die Seite es einlöst. (Nacharbeit 24.09.2026,
 * Ticket 2536)
 *
 * Bis zur Nacharbeit standen hier zwei lose Variablen in app.ts
 * (`wachwortVorgemerkt`, `wachwortSatzVorgemerkt`), und das Alter zählte ab
 * der ANKUNFT des Ereignisses — geprüft wurde es nur, wenn die Seite beim
 * Eintreffen unsichtbar war.
 *
 * ⚠️ Kommt das Ereignis erst beim Öffnen an (iOS hält die Seite im
 *    Hintergrund an und stellt die Nachricht nach), ist die Seite dann
 *    schon sichtbar. Der alte sichtbare Zweig sendete ohne jede Frist — ein
 *    Satz von vor Minuten wäre abgegangen. Jetzt zählt das Alter ab dem
 *    Satzende AUF DEM GERÄT (`WachwortSatz.am`), in beiden Zweigen, an
 *    dieser einen Stelle. Ohne `am` (ältere Builds, reiner Anlass) zählt die
 *    Ankunft wie bisher.
 * ⚠️ Einlösen heißt auch vergessen — ob gesendet wird oder nicht. Ein
 *    verfallener Satz wird nie gesendet und hängt an keiner späteren
 *    Vormerkung.
 */
export class WachwortVormerkung {
  private seit: number | null = null;
  private satz: WachwortSatz | null = null;

  /** Das Wort ist gefallen (mit oder ohne Satz). Ein älteres wird ersetzt. */
  merke(satz: WachwortSatz | null, jetzt: number): void {
    // Eine Geräteuhr „aus der Zukunft" zählt als jetzt, nicht als jünger.
    this.seit = satz && satz.am > 0 ? Math.min(satz.am, jetzt) : jetzt;
    this.satz = satz;
  }

  /** Wartet etwas darauf, eingelöst zu werden? */
  get offen(): boolean {
    return this.seit !== null;
  }

  /** Einlösen — und vergessen. `null`, wenn nichts vorgemerkt war. */
  nimm(jetzt: number): WachwortEinloesung | null {
    if (this.seit === null) return null;
    const nachMs = Math.max(0, jetzt - this.seit);
    const satz = this.satz;
    this.seit = null;
    this.satz = null;
    if (nachMs > WACHWORT_NACHHOLEN_MS) return { art: "verfallen", nachMs, feld: offenerWachwortSatz(satz) };
    return { art: "weiter", satz, nachMs };
  }
}

/** Die zwei Griffe, die der Hintergrundweg braucht. */
interface WachwortHintergrundWege {
  /**
   * Über pttSendeWachwortSatz (= pttSendBlob), ohne Mikrofon. `true` = der Versand ist angelaufen;
   * `false` = der Sendeweg hat abgelehnt (belegt) — dann ändert sich nichts, der Satz bleibt vorgemerkt.
   */
  sende(s: { ton: Blob; mime: string; text: string; dauerMs: number }): boolean;
  /** Eine Zeile ins Diagnoseprotokoll — nur Zahlen und Gründe, nie der Text. */
  melde(zeile: string): void;
  /**
   * Prüfauflage H1 (20.10.2026): ein offener Satz kommt SOFORT ins Eingabefeld (das Feld braucht kein Mikro). Fehlt der
   * Weg, bleibt er vorgemerkt — das Einlösen legt ihn dann ins Feld, auch nach der Frist (`wachwortFeldText`).
   */
  insFeld?(text: string): void;
}

/**
 * „Hey Vespera, …" bei verdeckter App — der Satz geht SOFORT ab. (13.10.2026)
 *
 * Martin im Auto, Navigation vorn: „Wenn etwas im Vordergrund ist, kann ich nicht mehr mit Vespera reden."
 * Der Lauscher hört das Wort auch dort und bringt den Satz seit Build 77 mit. Das Vormerken bis zum Öffnen
 * hatte einen Grund: iOS lässt im Hintergrund KEIN NEUES Mikro beginnen (PawbotWachWort.swift, „Das Mikrofon
 * halten"). Ein schon aufgenommener Satz braucht keines — er geht über denselben Sendeweg wie im Vordergrund.
 *
 * ⚠️ Vorgemerkt wird IMMER zuerst (wie bisher). Erst wenn der Sendeweg den Satz angenommen hat, ist die
 *    Vormerkung eingelöst (`nimm`) — so geht nichts doppelt ab, wenn die App später sichtbar wird.
 * ⚠️ Ohne brauchbaren Satz (kein Text, zu kurz, Phantom, Anruf-Modus, wartendes Bild) bleibt es bei der
 *    Vormerkung: das Mikro kann im Hintergrund nicht öffnen, es öffnet beim Sichtbarwerden.
 * ⚠️ Scheitert der Versand NACH dem Start (Netz), liegt die Aufnahme im Speicher der Seite und die Karte
 *    „Erneut senden“ steht im Chat — die Vormerkung würde hier doppelt senden, wo der Server den Satz schon
 *    haben kann.
 */
export function wachwortImHintergrund(
  satz: WachwortSatz | null,
  lage: WachwortLage,
  vormerkung: WachwortVormerkung,
  wege: WachwortHintergrundWege,
  jetzt: number,
  alsBlob: (b64: string, mime: string) => Blob = base64ZuBlob,
): "gesendet" | "vorgemerkt" | "verfallen" | "feld" {
  vormerkung.merke(satz, jetzt);
  if (!satz) return "vorgemerkt";
  const zeichen = satz.text.trim().length;
  const plan = wachwortPlan(satz, lage, alsBlob);
  if (plan.art === "feld" && wege.insFeld) {
    vormerkung.nimm(jetzt);
    wege.melde(`[wachwort] hintergrund-feld grund=unvollstaendig zeichen=${zeichen}`);
    wege.insFeld(plan.text);
    return "feld";
  }
  if (plan.art !== "senden") {
    wege.melde(`[wachwort] hintergrund-vorgemerkt grund=${plan.art === "mikro" ? plan.grund : "unvollstaendig"} zeichen=${zeichen}`);
    return "vorgemerkt";
  }
  const e = vormerkung.nimm(jetzt);
  if (!e || e.art === "verfallen") {
    wege.melde(`[wachwort] hintergrund-abgelehnt grund=verfallen nach=${Math.round((e?.nachMs ?? 0) / 1000)}s`);
    return "verfallen";
  }
  wege.melde(`[wachwort] hintergrund-gesendet satz=${zeichen} ton=${plan.dauerMs}ms nach=${Math.round(e.nachMs / 1000)}s`);
  let angelaufen = false;
  try {
    angelaufen = wege.sende({ ton: plan.ton, mime: plan.mime, text: plan.text, dauerMs: plan.dauerMs });
  } catch {
    angelaufen = false;
  }
  if (!angelaufen) {
    // Rückfall: wieder vormerken (mit dem Alter ab Satzende), das Öffnen löst es noch ein.
    vormerkung.merke(satz, jetzt);
    wege.melde("[wachwort] hintergrund-ergebnis abgelehnt grund=sendeweg-belegt — bleibt vorgemerkt");
    return "vorgemerkt";
  }
  wege.melde("[wachwort] hintergrund-ergebnis gesendet");
  return "gesendet";
}
